import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../../../services/Api";

const SincronizacaoFaturamentoContext = createContext();

const INTERVALO_POLLING = 5000;
const CHAVE_EXECUCOES_DISPENSADAS =
  "@DNACenter:faturamento:sincronizacoes-dispensadas";
const LIMITE_EXECUCOES_DISPENSADAS = 20;
const STATUS_EM_ANDAMENTO = "EM_ANDAMENTO";
const STATUS_FINALIZADOS = ["CONCLUIDA", "FALHOU"];

const TIPOS_SINCRONIZACAO = {
  ATUALIZACAO_GERAL: "ATUALIZACAO_GERAL",
  SINCRONIZACAO_INDIVIDUAL: "SINCRONIZACAO_INDIVIDUAL",
};

const normalizarValor = (valor) => String(valor ?? "").trim().toUpperCase();

const obterTipoExecucao = (execucao) =>
  execucao?.tipo ?? execucao?.tipo_operacao ?? "";

const obterCodigoLote = (execucao) =>
  execucao?.codigo_lote ?? execucao?.lote ?? "";

const obterConvenio = (execucao) =>
  execucao?.convenio ?? execucao?.plano ?? "";

const obterIdLote = (execucao) =>
  execucao?.id_lote ?? execucao?.filtros?.id_lote ?? null;

const obterChaveOperacao = (execucao) => {
  const tipo = normalizarValor(obterTipoExecucao(execucao));

  if (tipo === TIPOS_SINCRONIZACAO.ATUALIZACAO_GERAL) {
    return tipo;
  }

  const idLote = obterIdLote(execucao);

  return [
    tipo,
    idLote !== null && idLote !== undefined ? `ID:${idLote}` : "SEM_ID",
    normalizarValor(obterCodigoLote(execucao)),
    normalizarValor(obterConvenio(execucao)),
  ].join(":");
};

const obterIdentificadorExecucao = (execucao) => {
  if (execucao?.id !== undefined && execucao?.id !== null) {
    return `id:${execucao.id}`;
  }

  if (execucao?.execucao_id !== undefined && execucao?.execucao_id !== null) {
    return `id:${execucao.execucao_id}`;
  }

  return [
    obterChaveOperacao(execucao),
    execucao?.iniciado_em ?? "",
    execucao?.finalizado_em ?? "",
  ].join(":");
};

const obterArmazenamentoDaSessao = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (erro) {
    return null;
  }
};

const carregarExecucoesDispensadas = () => {
  const armazenamento = obterArmazenamentoDaSessao();

  if (!armazenamento) {
    return [];
  }

  try {
    const identificadores = JSON.parse(
      armazenamento.getItem(CHAVE_EXECUCOES_DISPENSADAS) ?? "[]",
    );

    if (!Array.isArray(identificadores)) {
      return [];
    }

    return identificadores
      .filter((identificador) => typeof identificador === "string")
      .slice(-LIMITE_EXECUCOES_DISPENSADAS);
  } catch (erro) {
    return [];
  }
};

const salvarExecucoesDispensadas = (identificadores) => {
  const armazenamento = obterArmazenamentoDaSessao();

  if (!armazenamento) {
    return;
  }

  try {
    armazenamento.setItem(
      CHAVE_EXECUCOES_DISPENSADAS,
      JSON.stringify(identificadores),
    );
  } catch (erro) {
    // O estado do contexto mantém a dispensa enquanto a instância estiver ativa.
  }
};

const ehExecucaoFinalizada = (execucao) =>
  STATUS_FINALIZADOS.includes(execucao?.status);

const ehMesmaOperacao = (execucaoA, execucaoB) => {
  if (
    normalizarValor(obterTipoExecucao(execucaoA)) !==
    normalizarValor(obterTipoExecucao(execucaoB))
  ) {
    return false;
  }

  const idLoteA = obterIdLote(execucaoA);
  const idLoteB = obterIdLote(execucaoB);

  if (
    idLoteA !== null &&
    idLoteA !== undefined &&
    idLoteB !== null &&
    idLoteB !== undefined
  ) {
    return String(idLoteA) === String(idLoteB);
  }

  return (
    normalizarValor(obterCodigoLote(execucaoA)) ===
      normalizarValor(obterCodigoLote(execucaoB)) &&
    normalizarValor(obterConvenio(execucaoA)) ===
      normalizarValor(obterConvenio(execucaoB))
  );
};

const obterDataOrdenacao = (execucao) => {
  const data =
    execucao?.finalizado_em ?? execucao?.iniciado_em ?? execucao?.criado_em;
  const timestamp = data ? new Date(data).getTime() : 0;

  if (!Number.isNaN(timestamp) && timestamp > 0) {
    return timestamp;
  }

  return Number(execucao?.id) || 0;
};

const ordenarExecucoes = (execucoes) =>
  [...execucoes].sort(
    (execucaoA, execucaoB) =>
      obterDataOrdenacao(execucaoB) - obterDataOrdenacao(execucaoA),
  );

const normalizarParaComparacao = (valor) => {
  if (Array.isArray(valor)) {
    return valor.map(normalizarParaComparacao);
  }

  if (valor && typeof valor === "object") {
    return Object.keys(valor)
      .sort()
      .reduce((objeto, chave) => {
        objeto[chave] = normalizarParaComparacao(valor[chave]);
        return objeto;
      }, {});
  }

  return valor;
};

const possuemMesmoConteudo = (valorAtual, proximoValor) =>
  JSON.stringify(normalizarParaComparacao(valorAtual)) ===
  JSON.stringify(normalizarParaComparacao(proximoValor));

const normalizarListaExecucoes = (dados) => {
  let execucoes = [];

  if (Array.isArray(dados)) {
    execucoes = dados;
  } else if (Array.isArray(dados?.data)) {
    execucoes = dados.data;
  } else if (Array.isArray(dados?.execucoes)) {
    execucoes = dados.execucoes;
  } else if (Array.isArray(dados?.data?.data)) {
    execucoes = dados.data.data;
  }

  return execucoes.map(normalizarExecucao).filter(Boolean);
};

function normalizarExecucao(dados) {
  let execucao = dados?.data ?? dados;

  if (execucao?.execucao) {
    execucao = execucao.execucao;
  }

  if (execucao?.data && !Array.isArray(execucao.data)) {
    execucao = execucao.data.execucao ?? execucao.data;
  }

  if (!execucao || typeof execucao !== "object" || Array.isArray(execucao)) {
    return null;
  }

  let resultado = execucao.resultado;

  if (typeof resultado === "string") {
    try {
      resultado = JSON.parse(resultado);
    } catch (erro) {
      resultado = null;
    }
  }

  if (resultado && typeof resultado === "object" && !Array.isArray(resultado)) {
    execucao = { ...resultado, ...execucao, resultado };
  }

  const naoLocalizadoNaOrigem =
    execucao.status === "FALHOU" &&
    Number(execucao.total_erro) === 0 &&
    /lote.*n[aã]o encontr|n[aã]o localizado.*softlab/i.test(
      execucao.mensagem ?? "",
    );

  if (naoLocalizadoNaOrigem && !execucao.situacao) {
    const item = {
      codigo: execucao.codigo_lote ?? execucao.lote ?? null,
      convenio: execucao.convenio ?? execucao.plano ?? null,
      situacao: "NAO_LOCALIZADO_NA_ORIGEM",
      motivo: execucao.mensagem,
    };

    execucao = {
      ...execucao,
      situacao: "NAO_LOCALIZADO_NA_ORIGEM",
      total_pendente_origem: 1,
      itens: [item],
      observacoes: [execucao.mensagem],
    };
  }

  if (execucao.execucao_id && !execucao.id) {
    return { ...execucao, id: execucao.execucao_id };
  }

  return execucao;
}

const obterMensagemErro = (erro, mensagemPadrao) => {
  const dados = erro?.response?.data;
  const mensagem =
    dados?.mensagem ??
    dados?.message ??
    dados?.erro ??
    dados?.error ??
    erro?.message;

  return typeof mensagem === "string" && mensagem.trim()
    ? mensagem
    : mensagemPadrao;
};

function SincronizacaoFaturamentoContextProvider({ children }) {
  const [execucoesPersistidas, setExecucoesPersistidas] = useState([]);
  const [execucoesOtimistas, setExecucoesOtimistas] = useState([]);
  const [erroAcompanhamento, setErroAcompanhamento] = useState("");
  const [carregandoInicial, setCarregandoInicial] = useState(true);
  const [revisaoListagem, setRevisaoListagem] = useState(0);
  const [execucoesDispensadas, setExecucoesDispensadas] = useState(
    carregarExecucoesDispensadas,
  );

  const montadoRef = useRef(true);
  const sequenciaOtimistaRef = useRef(0);
  const statusConhecidosRef = useRef(new Map());
  const finalizacoesNotificadasRef = useRef(new Set());
  const carregamentoInicialConcluidoRef = useRef(false);

  const notificarFinalizacao = useCallback((execucao) => {
    if (!ehExecucaoFinalizada(execucao)) {
      return;
    }

    const identificador = obterIdentificadorExecucao(execucao);

    if (finalizacoesNotificadasRef.current.has(identificador)) {
      return;
    }

    finalizacoesNotificadasRef.current.add(identificador);
    setRevisaoListagem((revisaoAtual) => revisaoAtual + 1);
  }, []);

  const aplicarExecucoesPersistidas = useCallback(
    (execucoesRecebidas) => {
      const execucoesOrdenadas = ordenarExecucoes(execucoesRecebidas);
      const proximosStatus = new Map();

      execucoesOrdenadas.forEach((execucao) => {
        const identificador = obterIdentificadorExecucao(execucao);
        const chaveOperacao = obterChaveOperacao(execucao);
        const statusAnterior =
          statusConhecidosRef.current.get(identificador) ??
          statusConhecidosRef.current.get(chaveOperacao);

        if (
          carregamentoInicialConcluidoRef.current &&
          ehExecucaoFinalizada(execucao) &&
          (statusAnterior === STATUS_EM_ANDAMENTO ||
            !finalizacoesNotificadasRef.current.has(identificador))
        ) {
          notificarFinalizacao(execucao);
        }

        if (!carregamentoInicialConcluidoRef.current && ehExecucaoFinalizada(execucao)) {
          finalizacoesNotificadasRef.current.add(identificador);
        }

        proximosStatus.set(identificador, execucao.status);

        if (!proximosStatus.has(chaveOperacao)) {
          proximosStatus.set(chaveOperacao, execucao.status);
        }
      });

      statusConhecidosRef.current = proximosStatus;
      carregamentoInicialConcluidoRef.current = true;

      if (!montadoRef.current) {
        return;
      }

      setExecucoesPersistidas((execucoesAtuais) =>
        possuemMesmoConteudo(execucoesAtuais, execucoesOrdenadas)
          ? execucoesAtuais
          : execucoesOrdenadas,
      );
      setExecucoesOtimistas((execucoesAtuais) => {
        const execucoesRestantes = execucoesAtuais.filter((execucaoOtimista) => {
          const inicioOtimista = obterDataOrdenacao(execucaoOtimista);

          return !execucoesOrdenadas.some((execucaoPersistida) => {
            if (!ehMesmaOperacao(execucaoOtimista, execucaoPersistida)) {
              return false;
            }

            return (
              execucaoPersistida.status === STATUS_EM_ANDAMENTO ||
              obterDataOrdenacao(execucaoPersistida) >= inicioOtimista - 2000
            );
          });
        });

        return execucoesRestantes.length === execucoesAtuais.length
          ? execucoesAtuais
          : execucoesRestantes;
      });
    },
    [notificarFinalizacao],
  );

  const revalidarExecucoes = useCallback(async () => {
    try {
      const { data } = await api.get("/v2/faturamento/sincronizacoes", {
        params: { limite: 20 },
      });

      aplicarExecucoesPersistidas(normalizarListaExecucoes(data));

      if (montadoRef.current) {
        setErroAcompanhamento("");
      }
    } catch (erro) {
      if (montadoRef.current) {
        setErroAcompanhamento(
          "O acompanhamento das sincronizações está temporariamente indisponível.",
        );
      }
    } finally {
      if (montadoRef.current) {
        setCarregandoInicial(false);
      }
    }
  }, [aplicarExecucoesPersistidas]);

  useEffect(() => {
    montadoRef.current = true;
    let intervaloId;

    const paginaEstaVisivel = () =>
      typeof document === "undefined" || document.visibilityState !== "hidden";

    const interromperPolling = () => {
      if (intervaloId) {
        clearInterval(intervaloId);
        intervaloId = undefined;
      }
    };

    const iniciarPolling = () => {
      interromperPolling();

      if (paginaEstaVisivel()) {
        intervaloId = setInterval(revalidarExecucoes, INTERVALO_POLLING);
      }
    };

    const aoAlterarVisibilidade = () => {
      if (paginaEstaVisivel()) {
        revalidarExecucoes();
        iniciarPolling();
      } else {
        interromperPolling();
      }
    };

    const aoRecuperarFoco = () => {
      revalidarExecucoes();
    };

    revalidarExecucoes();
    iniciarPolling();
    window.addEventListener("focus", aoRecuperarFoco);
    document.addEventListener("visibilitychange", aoAlterarVisibilidade);

    return () => {
      montadoRef.current = false;
      interromperPolling();
      window.removeEventListener("focus", aoRecuperarFoco);
      document.removeEventListener("visibilitychange", aoAlterarVisibilidade);
    };
  }, [revalidarExecucoes]);

  const registrarInicio = useCallback((detalhes) => {
    sequenciaOtimistaRef.current += 1;

    const execucaoOtimista = {
      ...detalhes,
      id: `otimista-${Date.now()}-${sequenciaOtimistaRef.current}`,
      status: STATUS_EM_ANDAMENTO,
      iniciado_em: new Date().toISOString(),
      mensagem: detalhes.mensagem ?? "Iniciando sincronização...",
      otimista: true,
    };

    statusConhecidosRef.current.set(
      obterChaveOperacao(execucaoOtimista),
      STATUS_EM_ANDAMENTO,
    );
    setExecucoesOtimistas((execucoesAtuais) => [
      execucaoOtimista,
      ...execucoesAtuais.filter(
        (execucaoAtual) => !ehMesmaOperacao(execucaoAtual, execucaoOtimista),
      ),
    ]);
    setErroAcompanhamento("");

    return execucaoOtimista;
  }, []);

  const cancelarInicio = useCallback(
    (execucaoOtimista) => {
      setExecucoesOtimistas((execucoesAtuais) =>
        execucoesAtuais.filter(
          (execucaoAtual) => execucaoAtual.id !== execucaoOtimista?.id,
        ),
      );
      revalidarExecucoes();
    },
    [revalidarExecucoes],
  );

  const registrarResultado = useCallback(
    (dados, execucaoOtimista) => {
      const execucaoRecebida = normalizarExecucao(dados);

      if (!execucaoRecebida) {
        setExecucoesOtimistas((execucoesAtuais) =>
          execucoesAtuais.filter(
            (execucaoAtual) => execucaoAtual.id !== execucaoOtimista?.id,
          ),
        );
        revalidarExecucoes();
        return null;
      }

      const execucao = {
        ...execucaoOtimista,
        ...execucaoRecebida,
        id:
          execucaoRecebida.id ??
          execucaoRecebida.execucao_id ??
          execucaoOtimista?.id,
        status: execucaoRecebida.status ?? "CONCLUIDA",
        otimista: false,
      };
      const identificador = obterIdentificadorExecucao(execucao);

      setExecucoesOtimistas((execucoesAtuais) =>
        execucoesAtuais.filter(
          (execucaoAtual) =>
            execucaoAtual.id !== execucaoOtimista?.id &&
            !ehMesmaOperacao(execucaoAtual, execucao),
        ),
      );
      setExecucoesPersistidas((execucoesAtuais) =>
        ordenarExecucoes([
          execucao,
          ...execucoesAtuais.filter(
            (execucaoAtual) =>
              obterIdentificadorExecucao(execucaoAtual) !== identificador,
          ),
        ]),
      );

      statusConhecidosRef.current.set(identificador, execucao.status);
      statusConhecidosRef.current.set(
        obterChaveOperacao(execucao),
        execucao.status,
      );
      notificarFinalizacao(execucao);
      revalidarExecucoes();

      return execucao;
    },
    [notificarFinalizacao, revalidarExecucoes],
  );

  const registrarFalha = useCallback(
    (erro, execucaoOtimista) => {
      const dadosErro = erro?.response?.data ?? {};
      const statusHttp = erro?.response?.status;
      const naoLocalizadoNaOrigem = statusHttp === 404;
      const execucaoRecebida = normalizarExecucao(
        dadosErro.execucao ?? dadosErro,
      );
      const mensagem = obterMensagemErro(
        erro,
        naoLocalizadoNaOrigem
          ? "Lote não localizado no SoftLab."
          : "Não foi possível concluir a sincronização.",
      );
      const itemNaoLocalizado = naoLocalizadoNaOrigem
        ? {
            id_lote: execucaoOtimista?.id_lote ?? null,
            codigo:
              execucaoOtimista?.codigo_lote ?? execucaoOtimista?.lote ?? null,
            convenio:
              execucaoOtimista?.convenio ?? execucaoOtimista?.plano ?? null,
            situacao: "NAO_LOCALIZADO_NA_ORIGEM",
            motivo: mensagem,
          }
        : null;
      const execucao = {
        ...execucaoOtimista,
        ...execucaoRecebida,
        id:
          execucaoRecebida?.id ??
          dadosErro.execucao_id ??
          `falha-${Date.now()}`,
        status: "FALHOU",
        mensagem,
        ...(naoLocalizadoNaOrigem
          ? {
              situacao: "NAO_LOCALIZADO_NA_ORIGEM",
              total_encontrado: null,
              total_pendente_origem: 1,
              total_erro: 0,
              itens: [itemNaoLocalizado],
              observacoes: [mensagem],
            }
          : {}),
        finalizado_em:
          execucaoRecebida?.finalizado_em ?? new Date().toISOString(),
        otimista: false,
      };

      registrarResultado(execucao, execucaoOtimista);
      return execucao;
    },
    [registrarResultado],
  );

  const execucoes = useMemo(
    () => ordenarExecucoes([...execucoesOtimistas, ...execucoesPersistidas]),
    [execucoesOtimistas, execucoesPersistidas],
  );

  const execucoesAtivas = useMemo(
    () =>
      execucoes.filter((execucao) => execucao.status === STATUS_EM_ANDAMENTO),
    [execucoes],
  );

  const atualizacaoGeralAtiva = useMemo(
    () =>
      execucoesAtivas.find(
        (execucao) =>
          obterTipoExecucao(execucao) ===
          TIPOS_SINCRONIZACAO.ATUALIZACAO_GERAL,
      ),
    [execucoesAtivas],
  );

  const obterSincronizacaoLoteAtiva = useCallback(
    (lote, convenio, idLote = null) =>
      execucoesAtivas.find(
        (execucao) => {
          if (
            obterTipoExecucao(execucao) !==
            TIPOS_SINCRONIZACAO.SINCRONIZACAO_INDIVIDUAL
          ) {
            return false;
          }

          const idLoteExecucao = obterIdLote(execucao);

          if (
            idLote !== null &&
            idLote !== undefined &&
            idLoteExecucao !== null &&
            idLoteExecucao !== undefined
          ) {
            return String(idLoteExecucao) === String(idLote);
          }

          return (
            normalizarValor(obterCodigoLote(execucao)) ===
              normalizarValor(lote) &&
            normalizarValor(obterConvenio(execucao)) ===
              normalizarValor(convenio)
          );
        },
      ),
    [execucoesAtivas],
  );

  const ultimaExecucaoFinalizada = useMemo(
    () => execucoes.find(ehExecucaoFinalizada),
    [execucoes],
  );

  const ultimaExecucaoFinalizadaVisivel = useMemo(() => {
    if (!ultimaExecucaoFinalizada) {
      return null;
    }

    return execucoesDispensadas.includes(
      obterIdentificadorExecucao(ultimaExecucaoFinalizada),
    )
      ? null
      : ultimaExecucaoFinalizada;
  }, [execucoesDispensadas, ultimaExecucaoFinalizada]);

  const dispensarExecucao = useCallback((execucao) => {
    const identificador = obterIdentificadorExecucao(execucao);

    setExecucoesDispensadas((execucoesAtuais) => {
      if (execucoesAtuais.includes(identificador)) {
        return execucoesAtuais;
      }

      const proximasExecucoes = [...execucoesAtuais, identificador].slice(
        -LIMITE_EXECUCOES_DISPENSADAS,
      );

      salvarExecucoesDispensadas(proximasExecucoes);
      return proximasExecucoes;
    });
  }, []);

  return (
    <SincronizacaoFaturamentoContext.Provider
      value={{
        execucoesAtivas,
        ultimaExecucaoFinalizada,
        ultimaExecucaoFinalizadaVisivel,
        atualizacaoGeralAtiva,
        erroAcompanhamento,
        carregandoInicial,
        revisaoListagem,
        obterSincronizacaoLoteAtiva,
        registrarInicio,
        cancelarInicio,
        registrarResultado,
        registrarFalha,
        revalidarExecucoes,
        dispensarExecucao,
      }}
    >
      {children}
    </SincronizacaoFaturamentoContext.Provider>
  );
}

export {
  INTERVALO_POLLING,
  SincronizacaoFaturamentoContext,
  SincronizacaoFaturamentoContextProvider,
  TIPOS_SINCRONIZACAO,
  ehMesmaOperacao,
  normalizarExecucao,
  normalizarListaExecucoes,
  obterChaveOperacao,
  obterMensagemErro,
};
